// @ts-nocheck
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Box, Tooltip, Typography } from '@mui/material';

// Import all field components from the index file
import {
  TextFieldComponent,
  IntegerFieldComponent,
  DecimalFieldComponent,
  DateFieldComponent,
  DropdownFieldComponent,
  MultiSelectFieldComponent,
  UserSelectFieldComponent,
  GroupSelectFieldComponent,
  AssociationFieldComponent,
  SegmentsAttribute,
  AttributeListFieldComponent,
  ParameterTableFieldComponent,
  EmailTemplateListFieldComponent,
  TemplateListFieldComponent,
  FieldChangeActionsComponent,
  InventoryViewAttribute,
  AssociationFieldTableViewComponent,
} from './FieldComponents';
import MultiEditModal from './Modals/MultiEditModal';
import GroupMultiSelectFieldComponent from './FieldComponents/GroupMultiSelectFieldComponent';
import UserMultiSelectFieldComponent from './FieldComponents/UserMultiSelectFieldComponent';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../const/CommonConst';
import { checkAttributeIsRequired, isValueEmpty } from '../../../utils/Utils';
import { ReactComponent as CalculationIcon } from '../../../assets/images/CalculationIcon.svg';
import FieldChangeOtherDetails from './FieldComponents/FieldChangeOtherDetails';
import AISuggestionIndicator from '../AIAutofill/AISuggestionIndicator';
import { useUploadDocumentContext } from '../../../context/DocumentUpload/UploadDocumentContext';

// Data types that should open in modal instead of inline editing
const MODAL_DATA_TYPES = [
  'Table',
  'AdvancedTable',
  'Chart',
  'Checklist',
  'Document',
  'Paragraph',
];

// Input control types that should open in modal instead of inline editing
const MODAL_INPUT_TYPES = ['TEXTFIELD_EXTENDED'];

const attributeRequiresTeamUsers = ['assignedUser'];

/**
 * A component for multi-edit mode that renders inline fields for simple data types
 * and modal triggers for complex data types
 * @param {Object} props - Component props
 * @param {Object} props.attribute - The attribute object with metadata
 * @param {any} props.value - Current value of the attribute
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Array} props.usersList - List of users (for User type)
 * @param {Array} props.groupList - List of groups (for Group type)
 * @param {Array} props.associationList - List of associations (for Association type)
 * @param {Object} props.entityData - Entity data for document fields
 * @returns {React.ReactElement} The appropriate field component or modal trigger
 */
const MultiEditAttributeField = ({
  attribute,
  value,
  onChange,
  usersList = EMPTY_ARRAY,
  groupList = EMPTY_ARRAY,
  entityData = EMPTY_OBJECT,
  showLabel = true,
  entityDetailsFormikForm,
  teamUsersList = EMPTY_ARRAY,
  handleOpenDerivedModal,
  handleAutoSave,
  hideHeader,
  // Constraint-related props
  lookupSourceAttributes = EMPTY_ARRAY,
  autoCompleteSourceAttributes = EMPTY_ARRAY,
  overrideDropdownOptions = EMPTY_OBJECT,
  isLoadingConstraints = false,
  dateCalendarConstraints = EMPTY_OBJECT,
  dateCalendarSourceAttributes = EMPTY_ARRAY,
  editableSourceAttributes = EMPTY_ARRAY,
  mandatorySourceAttributes = EMPTY_ARRAY,
  groupDetails = EMPTY_OBJECT,
  // Explicit props for SegmentsAttribute and others
  isUserAuthorizedForUpdateAttribute,
  breadcrumbDetails,
  deleteNote,
  editNote,
  handleOpenNoteModal,
  handleSetAddNoteModalHeading,
  handleSetSelectedEntityToAddInEditor,
  getAttComments,
  allAttributeChats,
  getOverrideAttributeValue,
  isMultipleEditMode,
  createNewSegmentRef,
  copySegmentRef,
  segmentButtonState,
  entitySelectedGroupId,
  prevNodeGroupSelected,
  attributeValues,
  // AI Autofill props
  aiSuggestion,
  onAcceptAISuggestion,
  onRejectAISuggestion,
  onUndoAISuggestion,
  configColumn = false,
  modelTemplateDetails = EMPTY_OBJECT,
}) => {
  const { pluginConfig } = useSelector((state) => state?.tenant);
  const isHDFCUAMEnabled = useMemo(
    () => !!pluginConfig?.HDFCUAM,
    [pluginConfig]
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const uploadContext = useUploadDocumentContext();
  const activeBatches = uploadContext?.activeBatches || [];
  const uploadProgress = uploadContext?.uploadProgress || {};

  const uploadErrors = uploadContext?.uploadErrors || {};

  /**
   * Derive detailed upload status for this document attribute:
   * - progress: number (0-100)
   * - hasError: boolean
   * - isUploading: boolean (file is in progress, not yet 100%)
   * - isProcessing: boolean (progress=100, no error, waiting for SSE)
   * - fileName: string
   */
  const docUploadStatus = useMemo(() => {
    if (attribute?.data_type !== 'Document') return null;
    for (const batch of activeBatches) {
      const isEntityMatch =
        String(batch.entityId) === String(entityData?.entity_id) ||
        String(batch.uniqueId) === String(entityData?.unique_id);

      if (!isEntityMatch) continue;

      for (const file of batch.data || []) {
        if (
          file.attribute_id === attribute?.id &&
          file.document_from_attribute
        ) {
          const progress = uploadProgress[file.key] || 0;
          const hasError = !!uploadErrors[file.key];
          const isQueued = uploadProgress[file.key] === undefined && !hasError;
          return {
            progress,
            hasError,
            isUploading: !isQueued && progress < 100 && !hasError,
            isProcessing: progress === 100 && !hasError,
            isQueued,
            fileName: file.document_name || file.file?.name || 'Document',
          };
        }
      }
    }
    return null;
  }, [attribute, activeBatches, uploadProgress, uploadErrors, entityData]);

  const isRequired = useMemo(() => {
    return checkAttributeIsRequired(attribute);
  }, [attribute]);

  const isLoadingFieldExceptSourceAttributes = useMemo(() => {
    // If the current attribute is a lookup source attribute, don't show loading
    if (
      lookupSourceAttributes?.includes(Number.parseInt(attribute?.id)) ||
      autoCompleteSourceAttributes?.includes(Number.parseInt(attribute?.id)) ||
      dateCalendarSourceAttributes?.includes(Number.parseInt(attribute?.id)) ||
      editableSourceAttributes?.includes(Number.parseInt(attribute?.id)) ||
      mandatorySourceAttributes?.includes(Number.parseInt(attribute?.id))
    ) {
      return false;
    }
    // For other attributes, return the loading state
    return isLoadingConstraints;
  }, [
    lookupSourceAttributes,
    autoCompleteSourceAttributes,
    dateCalendarSourceAttributes,
    editableSourceAttributes,
    mandatorySourceAttributes,
    isLoadingConstraints,
    attribute?.id,
  ]);

  const finalUsersList = useMemo(() => {
    if (
      attribute?.data_type === 'User' &&
      attributeRequiresTeamUsers.includes(attribute?.name)
    ) {
      return teamUsersList;
    }
    return usersList;
  }, [attribute?.data_type, attribute?.name, usersList, teamUsersList]);

  const dataType = attribute?.data_type;
  const inputType = attribute?.input_control_type;
  const label = attribute?.label;
  const isDerived = hideHeader
    ? attribute?.is_derived && attribute?.permission === 'manage'
    : attribute?.is_derived;
  const isDerivedExtended = useMemo(() => {
    return isDerived && inputType === 'TEXTFIELD_EXTENDED';
  }, [isDerived, inputType]);
  // Get override dropdown options for this attribute if available
  const attributeOverrideOptions = useMemo(() => {
    const attributeId = attribute?.id;
    return overrideDropdownOptions[attributeId] || null;
  }, [overrideDropdownOptions, attribute?.id]);

  // Check if this data type should open in modal
  const shouldOpenInModal = useMemo(() => {
    return (
      MODAL_DATA_TYPES.includes(dataType) ||
      MODAL_INPUT_TYPES.includes(inputType)
    );
  }, [dataType, inputType]);

  // Handle opening modal
  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, [setIsModalOpen]);

  // Handle closing modal
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  // Handle saving from modal
  const handleModalSave = useCallback(
    (newValue) => {
      onChange(newValue);
    },
    [onChange]
  );

  const hasError = useMemo(() => {
    const fieldId = attribute?.id;
    if (!fieldId || !entityDetailsFormikForm) return false;

    const { errors, touched } = entityDetailsFormikForm;

    return Boolean(errors?.[fieldId] && touched?.[fieldId]);
  }, [attribute?.id, entityDetailsFormikForm]);

  // Get modal button text based on data type and input type
  const getModalButtonText = useCallback(() => {
    const currentLabel = attribute?.label || attribute?.name;
    const hasValue = !isValueEmpty(value);
    if (inputType === 'TEXTFIELD_EXTENDED') {
      if (isDerivedExtended) return `View ${currentLabel}`;
      return hasValue ? `Edit - ${value}` : `Add ${currentLabel}`;
    }

    if (dataType === 'Table') {
      return isValueEmpty(value) ? 'Upload Table' : 'Edit Table';
    }
    if (dataType === 'AdvancedTable') {
      return 'Edit Advanced Table';
    }
    if (dataType === 'Chart') {
      return 'Edit Chart';
    }
    if (dataType === 'Checklist') {
      return 'Edit Checklist';
    }
    if (dataType === 'Document') {
      if (docUploadStatus) {
        if (docUploadStatus.hasError) {
          return 'Upload failed';
        }
        if (docUploadStatus.isProcessing) {
          return 'Processing…';
        }
        if (docUploadStatus.isQueued) {
          return 'Upload in progress…';
        }
        if (docUploadStatus.isUploading) {
          return `Uploading… ${Math.round(docUploadStatus.progress)}%`;
        }
      }
      if (!isValueEmpty(value) && value?.[0]?.document_name) {
        return value[0].document_name;
      }
      return isValueEmpty(value) ? 'Upload Document' : 'Edit Document';
    }
    if (dataType === 'Paragraph') {
      return 'Edit Paragraph';
    }

    return `Edit ${attribute?.label || attribute?.name}`;
  }, [
    dataType,
    inputType,
    attribute?.label,
    attribute?.name,
    value,
    isDerivedExtended,
    docUploadStatus,
  ]);
  const handleClickAttribute = useCallback(
    (e) => {
      e.stopPropagation();
      handleOpenDerivedModal(attribute);
    },
    [handleOpenDerivedModal, attribute]
  );
  // Render modal trigger for complex data types
  const renderModalTrigger = useCallback(() => {
    return (
      <>
        <Box display="flex" flexDirection="column">
          {showLabel && (
            <Box
              display="flex"
              alignItems="center"
              maxWidth="100%"
              title={label}
            >
              <Typography
                variant="body4"
                sx={{
                  maxWidth: 360,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {label}
              </Typography>

              {isRequired && (
                <Box
                  component="span"
                  sx={{ color: 'error.main', ml: 0.5, flexShrink: 0 }}
                >
                  *
                </Box>
              )}
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              height: '36px',
              pr: 1.9,
            }}
          >
            <Typography
              variant="body2"
              component="span"
              onClick={handleOpenModal}
              sx={{
                color: hasError ? 'error.main' : 'primary.main',
                cursor: 'pointer',
                textDecoration: 'underline',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {getModalButtonText()}
            </Typography>
            {isDerivedExtended && (
              <Tooltip
                title={
                  !attribute?.show_calculation_popUp
                    ? 'Enable the calculation popup to calculate this attribute.'
                    : `Calculate ${attribute?.label}`
                }
              >
                <CalculationIcon
                  sx={{
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                  onClick={handleClickAttribute}
                />
              </Tooltip>
            )}
          </Box>
        </Box>

        {isModalOpen && (
          <MultiEditModal
            open={isModalOpen}
            handleClose={handleCloseModal}
            attribute={attribute}
            value={value}
            handleSave={handleModalSave}
            groupList={groupList}
            entityData={entityData}
            entityDetailsFormikForm={entityDetailsFormikForm}
            handleAutoSave={handleAutoSave}
            isDerivedExtended={isDerivedExtended}
            usersList={finalUsersList}
            teamUsersList={teamUsersList}
            mode="edit"
            configColumn={configColumn}
            modelTemplateDetails={modelTemplateDetails}
          />
        )}
      </>
    );
  }, [
    handleOpenModal,
    getModalButtonText,
    isModalOpen,
    handleCloseModal,
    attribute,
    value,
    handleModalSave,
    groupList,
    entityData,
    handleClickAttribute,
    isDerived,
    inputType,
    isDerivedExtended,
    modelTemplateDetails,
  ]);

  // Render inline field for simple data types
  const renderInlineField = useCallback(() => {
    if (!attribute) return null;

    const isLongLabel = attribute?.label?.length > 90;
    const fieldLabel = isLongLabel
      ? attribute?.label?.slice(0, 90) + '...'
      : attribute?.label;

    const renderedLabel = (
      <Box display="flex" alignItems="center" maxWidth={280}>
        <Typography
          variant="body4"
          sx={{
            maxWidth: 360,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={attribute?.label}
        >
          {fieldLabel}
        </Typography>

        {isRequired && (
          <Box
            component="span"
            sx={{ color: 'error.main', ml: 0.5, flexShrink: 0 }}
          >
            *
          </Box>
        )}
      </Box>
    );

    const fieldProps = {
      label: renderedLabel,
      titleLabel: attribute?.label,
      value,
      onChange,
      attribute,
      showLabel,
      entityDetailsFormikForm,
      handleOpenDerivedModal,
      hideHeader,
      isLoadingConstraints: isLoadingFieldExceptSourceAttributes,
      isMultipleEditMode,
      configColumn,
      modelTemplateDetails,
    };

    // Check for 'Segments' (case-insensitive)
    if (dataType?.toLowerCase() === 'segments') {
      return (
        <SegmentsAttribute
          {...fieldProps}
          entityData={entityData}
          allUserList={usersList}
          teamUsersList={teamUsersList}
          groupList={groupList}
          isUserAuthorizedForUpdateAttribute={
            isUserAuthorizedForUpdateAttribute
          }
          breadcrumbDetails={breadcrumbDetails}
          deleteNote={deleteNote}
          editNote={editNote}
          handleOpenNoteModal={handleOpenNoteModal}
          handleSetAddNoteModalHeading={handleSetAddNoteModalHeading}
          handleSetSelectedEntityToAddInEditor={
            handleSetSelectedEntityToAddInEditor
          }
          getAttComments={getAttComments}
          allAttributeChats={allAttributeChats}
          getOverrideAttributeValue={getOverrideAttributeValue}
          isMultipleEditMode={isMultipleEditMode}
          createNewSegmentRef={createNewSegmentRef}
          copySegmentRef={copySegmentRef}
          segmentButtonState={segmentButtonState}
          entitySelectedGroupId={entitySelectedGroupId}
          prevNodeGroupSelected={prevNodeGroupSelected}
        />
      );
    }
    if (dataType?.toLowerCase() === 'fieldchange') {
      return (
        <FieldChangeOtherDetails
          {...fieldProps}
          usersList={finalUsersList}
          teamUsersList={teamUsersList}
          groupList={groupList}
          mode={value?.disableEdit ? 'view' : 'edit'}
        />
      );
    }
    if (dataType === 'AttributeList') {
      return <AttributeListFieldComponent {...fieldProps} />;
    }
    if (dataType === 'ParameterTableList') {
      return <ParameterTableFieldComponent {...fieldProps} />;
    }
    if (dataType === 'EmailTemplateList') {
      return <EmailTemplateListFieldComponent {...fieldProps} />;
    }
    if (dataType === 'TemplateList') {
      return <TemplateListFieldComponent {...fieldProps} />;
    }
    if (dataType === 'FieldChangeActions') {
      return <FieldChangeActionsComponent {...fieldProps} />;
    }
    if (dataType === 'InventoryView') {
      return (
        <InventoryViewAttribute
          {...fieldProps}
          entityId={entityData?.entity_id}
          entityType={entityData?.entity_type}
        />
      );
    }

    if (dataType === 'Character') {
      switch (inputType) {
        case 'TEXTFIELD':
          return <TextFieldComponent {...fieldProps} />;
        case 'SINGLE_SELECT':
          return (
            <DropdownFieldComponent
              {...fieldProps}
              overrideDropdownOptions={attributeOverrideOptions}
            />
          );
        case 'MULTI_SELECT':
          return (
            <MultiSelectFieldComponent
              {...fieldProps}
              overrideDropdownOptions={attributeOverrideOptions}
            />
          );
        default:
          return <TextFieldComponent {...fieldProps} />;
      }
    }

    if (dataType === 'User' && inputType === 'MULTI_SELECT') {
      return (
        <UserMultiSelectFieldComponent
          {...fieldProps}
          usersList={finalUsersList}
          formData={entityDetailsFormikForm?.values || {}}
          entityData={entityData}
        />
      );
    }

    if (dataType === 'Group' && inputType === 'MULTI_SELECT') {
      return (
        <GroupMultiSelectFieldComponent {...fieldProps} groupList={groupList} />
      );
    }

    // Handle other data types
    switch (dataType) {
      case 'Integer':
        return <IntegerFieldComponent {...fieldProps} />;
      case 'Decimal':
        return <DecimalFieldComponent {...fieldProps} />;
      case 'Date':
        return (
          <DateFieldComponent
            {...fieldProps}
            dateCalendarConstraints={dateCalendarConstraints}
          />
        );
      case 'User': {
        const userSelectField = (
          <UserSelectFieldComponent
            {...fieldProps}
            usersList={finalUsersList}
            groupDetails={groupDetails}
            formData={entityDetailsFormikForm?.values || {}}
            entityData={entityData}
            isMultipleEditMode={true}
            attributeValues={attributeValues}
          />
        );

        if (attribute?.name === 'assignedUser' && isHDFCUAMEnabled) {
          return (
            <Box display="flex" flexDirection="column" width="100%">
              {showLabel && renderedLabel}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  height: '36px',
                }}
              >
                {userSelectField}
              </Box>
            </Box>
          );
        }
        return userSelectField;
      }
      case 'Group':
        return (
          <GroupSelectFieldComponent {...fieldProps} groupList={groupList} />
        );
      case 'Association':
        if (attribute?.show_as_table) {
          return (
            <AssociationFieldTableViewComponent
              attribute={attribute}
              entityId={entityData?.entity_id}
              entityType={entityData?.entity_type}
            />
          );
        }
        return (
          <AssociationFieldComponent
            {...fieldProps}
            formData={entityDetailsFormikForm?.values || {}}
            entityData={entityData}
          />
        );
      case 'Hyperlink':
        return <TextFieldComponent {...fieldProps} />;
      case 'HTML':
        return (
          <>
            {showLabel && renderedLabel}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                height: '36px',
              }}
            >
              <Typography variant="body8" component="span">
                -
              </Typography>
            </Box>
          </>
        );
      default:
        // Handle special cases based on input control type
        if (inputType === 'IFRAME') {
          // For IFRAME type, show modal trigger even if not in MODAL_DATA_TYPES
          return renderModalTrigger();
        }
        return <TextFieldComponent {...fieldProps} />;
    }
  }, [
    attribute,
    value,
    onChange,
    finalUsersList,
    groupList,
    dataType,
    inputType,
    renderModalTrigger,
    handleOpenDerivedModal,
  ]);

  // Decide whether to render modal trigger or inline field
  if (shouldOpenInModal) {
    return renderModalTrigger();
  }

  const fieldContent = renderInlineField();

  // Wrap with AI suggestion indicator if there is a suggestion
  if (aiSuggestion) {
    return (
      <AISuggestionIndicator
        suggestion={aiSuggestion}
        attributeId={attribute?.id}
        onAccept={onAcceptAISuggestion}
        onReject={onRejectAISuggestion}
        onUndo={onUndoAISuggestion}
      >
        {fieldContent}
      </AISuggestionIndicator>
    );
  }

  return fieldContent;
};

MultiEditAttributeField.propTypes = {
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    label: PropTypes.string,
    data_type: PropTypes.string,
    input_control_type: PropTypes.string,
    is_derived: PropTypes.bool,
    permission: PropTypes.string,
    show_calculation_popUp: PropTypes.bool,
    show_as_table: PropTypes.bool,
  }).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.shape({}),
      ])
    ),
    PropTypes.shape({}),
  ]),
  onChange: PropTypes.func.isRequired,
  usersList: PropTypes.arrayOf(PropTypes.shape({})),
  groupList: PropTypes.arrayOf(PropTypes.shape({})),
  associationList: PropTypes.arrayOf(PropTypes.shape({})),
  entityData: PropTypes.shape({
    entity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entity_type: PropTypes.string,
  }),
  entityDetailsFormikForm: PropTypes.shape({
    getFieldProps: PropTypes.func,
    errors: PropTypes.objectOf(PropTypes.string),
    touched: PropTypes.objectOf(PropTypes.bool),
    values: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.arrayOf(
          PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.shape({}),
          ])
        ),
        PropTypes.shape({}),
      ])
    ),
  }),
  handleAutoSave: PropTypes.func,
  groupDetails: PropTypes.shape({
    isGroupAdmin: PropTypes.bool,
    userName: PropTypes.string,
    userId: PropTypes.number,
    users: PropTypes.arrayOf(
      PropTypes.shape({
        userId: PropTypes.number,
        username: PropTypes.string,
      })
    ),
  }),
  isUserAuthorizedForUpdateAttribute: PropTypes.bool,
  breadcrumbDetails: PropTypes.shape({}),
  deleteNote: PropTypes.func,
  editNote: PropTypes.func,
  handleOpenNoteModal: PropTypes.func,
  handleSetAddNoteModalHeading: PropTypes.func,
  handleSetSelectedEntityToAddInEditor: PropTypes.func,
  getAttComments: PropTypes.func,
  allAttributeChats: PropTypes.arrayOf(PropTypes.shape({})),
  getOverrideAttributeValue: PropTypes.func,
  isMultipleEditMode: PropTypes.bool,
  createNewSegmentRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.shape({}) }),
  ]),
  copySegmentRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.shape({}) }),
  ]),
  segmentButtonState: PropTypes.string,
  entitySelectedGroupId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  prevNodeGroupSelected: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  handleOpenDerivedModal: PropTypes.func,
  hideHeader: PropTypes.bool,
  lookupSourceAttributes: PropTypes.arrayOf(PropTypes.number),
  autoCompleteSourceAttributes: PropTypes.arrayOf(PropTypes.number),
  overrideDropdownOptions: PropTypes.shape({}),
  isLoadingConstraints: PropTypes.bool,
  dateCalendarConstraints: PropTypes.shape({}),
  dateCalendarSourceAttributes: PropTypes.arrayOf(PropTypes.number),
  editableSourceAttributes: PropTypes.arrayOf(PropTypes.number),
  mandatorySourceAttributes: PropTypes.arrayOf(PropTypes.number),
  teamUsersList: PropTypes.arrayOf(PropTypes.shape({})),
  showLabel: PropTypes.bool,
  attributeValues: PropTypes.shape({}),
  // AI Autofill
  aiSuggestion: PropTypes.shape({
    oldValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.shape({}),
    ]),
    newValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.shape({}),
    ]),
    confidence: PropTypes.number,
    status: PropTypes.string,
  }),
  onAcceptAISuggestion: PropTypes.func,
  onRejectAISuggestion: PropTypes.func,
  onUndoAISuggestion: PropTypes.func,
  configColumn: PropTypes.bool,
  modelTemplateDetails: PropTypes.shape({}),
};

export default MultiEditAttributeField;
